import { Icon } from '@mdvip/shared/components/blocks/Icon';
import {
  ResponsiveModal,
  ResponsiveModalBody,
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from '@mdvip/shared/components/blocks/ResponsiveModal';
import { Button } from '@mdvip/shared/components/ui/button';
import { FieldError } from '@mdvip/shared/components/ui/field';
import type { ChangeEvent, DragEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import Cropper, { type Area, type MediaSize, type Point } from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import { compositeCroppedPhoto } from '@/lib/profile/photoCrop';

export interface UpdateProfilePhotoDialogProps {
  open: boolean;
  onClose: () => void;
  /** No-op default in this item — the real, mutation-backed implementation lands in the
   * Save-wiring ticket. */
  onSave: (photo: Blob) => Promise<void>;
  isSaving?: boolean;
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ACCEPT_ATTR = 'image/jpeg,image/png,image/webp';
// Legacy's own literal cap (`MAX_FILE_SIZE = 2000000`) — decimal MB, not 2 * 1024 * 1024 (2 MiB).
const MAX_FILE_SIZE_BYTES = 2_000_000;
const MAX_FILE_SIZE_MB = MAX_FILE_SIZE_BYTES / 1_000_000;

type ValidationIssue = 'invalidType' | 'tooLarge';

function validateFile(file: File): ValidationIssue | null {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return 'invalidType';
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return 'tooLarge';
  }
  return null;
}

interface DialogContentProps {
  onClose: () => void;
  onSave: (photo: Blob) => Promise<void>;
  isSaving: boolean;
}

/**
 * Two steps in one dialog — choose a file, then frame it — matching the design's own
 * `ModalImageUpload`. The step is derived from whether an image has been picked (not a separate
 * flag), so there's nothing to keep in sync when a new file replaces the current one.
 *
 * Deliberately always opens on "choose", even when the member already has a photo — both the
 * design and legacy instead seed the position step straight from the existing photo, letting the
 * member re-frame it without picking a new file. That's not possible here yet: the existing photo
 * is served from a cross-origin SAS URL (Azure Blob Storage), and re-compositing it through
 * `compositeCroppedPhoto`'s canvas requires `crossOrigin="anonymous"` on the `<img>` *and* a
 * matching CORS grant from the storage account — confirmed empty (no rules at all) on the local
 * dev storage account. Legacy never had this problem because its photos were served same-origin.
 * Revisit once the storage account's CORS is configured for our origin(s).
 *
 * `UpdateProfilePhotoDialog` only mounts this when `open`, so every close discards all local
 * state (picked file, zoom, crop, error) for free — reopening always starts at "choose".
 */
function UpdateProfilePhotoDialogContent({ onClose, onSave, isSaving }: DialogContentProps) {
  const { t } = useTranslation('members');
  const [imageObjectUrl, setImageObjectUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [error, setError] = useState<string | null>(null);
  // react-easy-crop has no adaptive "cover" mode of its own — passing objectFit="cover" silently
  // falls through to its default "contain" behavior (confirmed in its source), which letterboxes
  // a non-square photo instead of filling the circular frame. Picking horizontal- vs
  // vertical-cover ourselves, once the image's own aspect ratio is known, is what actually fills
  // it edge-to-edge for both portrait and landscape source photos.
  const [mediaObjectFit, setMediaObjectFit] = useState<'horizontal-cover' | 'vertical-cover'>('horizontal-cover');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cropImageRef = useRef<HTMLImageElement | null>(null);
  // `isSaving` (the prop) only flips true once `onSave` is actually called below, which is after
  // the `await compositeCroppedPhoto(...)` — a second click landing in that gap sees `isSaving`
  // still false (the state update hasn't even been dispatched, let alone rendered) and fires a
  // second concurrent save. A ref is checked and set synchronously, before any `await`, so it
  // closes that gap in a way `isSaving`/`canSave` can't.
  const isSubmittingRef = useRef(false);

  const step: 'choose' | 'position' = imageObjectUrl ? 'position' : 'choose';

  const handleMediaLoaded = (mediaSize: MediaSize) => {
    setMediaObjectFit(mediaSize.naturalWidth > mediaSize.naturalHeight ? 'vertical-cover' : 'horizontal-cover');
  };

  // Object URLs aren't garbage-collected on their own — revoke the previous one whenever it's
  // replaced by a new pick, and on unmount (dialog close).
  useEffect(() => {
    return () => {
      if (imageObjectUrl) {
        URL.revokeObjectURL(imageObjectUrl);
      }
    };
  }, [imageObjectUrl]);

  const handleFile = (file: File | null | undefined) => {
    if (!file) {
      return;
    }
    const issue = validateFile(file);
    if (issue) {
      setError(t(`profile.photo.errors.${issue}`, { maxMB: MAX_FILE_SIZE_MB }));
      return;
    }
    setError(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
    setImageObjectUrl(URL.createObjectURL(file));
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFile(e.target.files?.[0]);
    // Clear so picking the same file again still fires onChange.
    e.target.value = '';
  };

  const handleDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleResetFraming = () => {
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };
  const isAtDefaultFraming = zoom === 1 && crop.x === 0 && crop.y === 0;

  const handleSave = async () => {
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    try {
      const image = cropImageRef.current;
      if (!croppedAreaPixels || !image) {
        return;
      }
      // Clear any error from a prior failed attempt — otherwise a retry that succeeds at
      // compositing but then hits a (silent) onSave rejection would still show the old
      // "couldn't process that photo" message, misattributing this attempt's failure.
      setError(null);
      let photo: Blob;
      try {
        photo = await compositeCroppedPhoto(image, croppedAreaPixels);
      } catch {
        // Unlike onSave below, nothing else owns this failure — surface it directly rather than
        // leaving the member with no feedback at all.
        setError(t('profile.photo.errors.cropFailed'));
        return;
      }
      try {
        await onSave(photo);
        onClose();
      } catch {
        // The caller's own mutation onError (per the Save-wiring ticket) surfaces the failure as a
        // toast — this dialog just stays open on the position step so the member can retry.
      }
    } finally {
      // Every path above (early return, crop failure, save failure) clears the guard — a
      // legitimate retry after a failed save still needs to be able to fire.
      isSubmittingRef.current = false;
    }
  };

  const canSave = step === 'position' && croppedAreaPixels != null && !isSaving;

  return (
    <>
      <ResponsiveModalHeader>
        <ResponsiveModalTitle>
          {step === 'choose' ? t('profile.photo.chooseTitle') : t('profile.photo.positionTitle')}
        </ResponsiveModalTitle>
        <ResponsiveModalDescription>
          {step === 'choose'
            ? t('profile.photo.chooseDescription', { maxMB: MAX_FILE_SIZE_MB })
            : t('profile.photo.positionDescription')}
        </ResponsiveModalDescription>
      </ResponsiveModalHeader>

      <ResponsiveModalBody>
        <input ref={fileInputRef} type="file" accept={ACCEPT_ATTR} className="hidden" onChange={handleInputChange} />

        {step === 'choose' ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="flex min-h-44 w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-input bg-muted p-8 text-center transition-colors hover:border-hairline-brand hover:bg-tint-brand-soft"
          >
            <Icon name="plus" size="xl" className="text-primary" />
            <span className="text-sm font-medium text-foreground">{t('profile.photo.dropzoneCta')}</span>
            <span className="text-xs text-muted-foreground">{t('profile.photo.dropzoneHint')}</span>
          </button>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <div className="relative mx-auto aspect-square w-44 shrink-0 touch-none select-none overflow-hidden rounded-full border border-border bg-muted">
              <Cropper
                image={imageObjectUrl ?? undefined}
                crop={crop}
                zoom={zoom}
                minZoom={1}
                maxZoom={3}
                keyboardStep={4}
                aspect={1}
                cropShape="round"
                objectFit={mediaObjectFit}
                showGrid={false}
                onMediaLoaded={handleMediaLoaded}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_area, areaPixels) => setCroppedAreaPixels(areaPixels)}
                setImageRef={(ref) => {
                  cropImageRef.current = ref.current;
                }}
                cropperProps={{ role: 'group', 'aria-label': t('profile.photo.frameLabel') }}
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-inset ring-border"
              />
            </div>
            <p className="text-center text-xs text-muted-foreground">
              {zoom > 1 ? t('profile.photo.dragHint') : t('profile.photo.zoomHint')}
            </p>
            <label className="flex w-full items-center gap-4 text-xs text-muted-foreground">
              {t('profile.photo.zoomLabel')}
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                aria-label={t('profile.photo.zoomLabel')}
                className="h-11 flex-1 accent-primary"
              />
            </label>
            <div className="flex w-full flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                className="min-h-11 flex-1"
                onClick={() => fileInputRef.current?.click()}
              >
                {t('profile.photo.changePhoto')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="min-h-11 sm:w-32"
                onClick={handleResetFraming}
                disabled={isAtDefaultFraming}
              >
                {t('profile.photo.reset')}
              </Button>
            </div>
          </div>
        )}

        <FieldError>{error}</FieldError>
      </ResponsiveModalBody>

      <ResponsiveModalFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
          {t('profile.cancel')}
        </Button>
        <Button type="button" onClick={handleSave} disabled={!canSave}>
          {isSaving ? t('profile.saving') : t('profile.photo.save')}
        </Button>
      </ResponsiveModalFooter>
    </>
  );
}

/**
 * The dialog the avatar's camera badge opens: pick an image, frame it (drag to reposition,
 * wheel/pinch/slider to zoom, circular crop), then Save or Cancel. Save here calls whatever the
 * caller passes — a no-op default in this item; the real mutation lands in the Save-wiring ticket.
 */
export function UpdateProfilePhotoDialog({ open, onClose, onSave, isSaving = false }: UpdateProfilePhotoDialogProps) {
  return (
    <ResponsiveModal
      open={open}
      // Escape and an outside click still fire onOpenChange(false) even with the close button
      // hidden (Base UI), so the in-flight guard has to live here too, not just on the button.
      onOpenChange={(next) => !next && !isSaving && onClose()}
      className="sm:max-w-md"
      showCloseButton={!isSaving}
    >
      {open && <UpdateProfilePhotoDialogContent onClose={onClose} onSave={onSave} isSaving={isSaving} />}
    </ResponsiveModal>
  );
}
